/**
 * O ícone que acompanha o cursor durante um arraste começado **no cenário**.
 *
 * ## Por que não é o arraste nativo do navegador
 *
 * O inventário arrasta com HTML5 (`dragstart`/`drop`) e continua assim: lá cada slot é um
 * elemento, e o navegador entrega ghost, alvo e transferência de dados de graça. O cenário
 * não tem elementos — é um canvas, e o item é um punhado de pixels dentro dele. Tornar o
 * canvas inteiro `draggable` transformaria todo clique no mundo num arraste em potencial e
 * levaria junto o clique de atacar, o de andar e o de abrir porta.
 *
 * Então o arraste que **nasce** no mundo é dirigido por ponteiro, e este módulo é a metade
 * visível dele: um `<div>` fixo na tela que segue o cursor. A metade invisível — descobrir o
 * que há sob o ponto em que o dedo soltou — mora em `inventory.ts` (`dropTargetAt`), porque é
 * de lá que vêm os atributos que ela lê.
 *
 * O elemento é criado uma vez e reaproveitado: um arraste que cria e destrói nó a cada gesto
 * é um gesto que às vezes começa com um quadro em branco.
 */
export class DragGhost {
  private readonly element: HTMLElement;
  private visible = false;

  constructor() {
    const element = document.createElement('div');
    element.className = 'drag-ghost';
    // Transparente ao ponteiro, senão o próprio ghost seria o que `elementFromPoint`
    // encontraria embaixo do cursor — e todo arraste terminaria em cima de si mesmo.
    element.style.display = 'none';
    document.body.append(element);

    this.element = element;
  }

  /** Começa a acompanhar o cursor. `icon` é a data URL da textura do item. */
  show(icon: string | undefined, count: number, clientX: number, clientY: number): void {
    this.element.replaceChildren();

    if (icon) {
      const image = document.createElement('img');
      image.src = icon;
      image.alt = '';
      this.element.append(image);
    }

    if (count > 1) {
      const label = document.createElement('span');
      label.className = 'drag-ghost-count';
      label.textContent = String(count);
      this.element.append(label);
    }

    this.element.style.display = 'block';
    this.visible = true;
    this.moveTo(clientX, clientY);
  }

  moveTo(clientX: number, clientY: number): void {
    if (!this.visible) return;
    // `translate` e não `left`/`top`: o ghost se move a cada quadro do arraste, e mexer em
    // propriedade de layout dispararia um reflow por movimento do mouse.
    this.element.style.transform = `translate(${clientX}px, ${clientY}px)`;
  }

  hide(): void {
    if (!this.visible) return;
    this.element.style.display = 'none';
    this.element.replaceChildren();
    this.visible = false;
  }

  /** O nó vive no `body`, fora da cena: quem o criou é quem tem de tirá-lo de lá. */
  destroy(): void {
    this.element.remove();
    this.visible = false;
  }
}

/**
 * O ghost da página inteira, criado na primeira vez que alguém arrasta.
 *
 * ## Por que um só
 *
 * Há **dois** gestos de arraste, nascidos em lugares diferentes: o do cenário, que a cena
 * dirige (ver `armDrag` em `game-scene.ts`), e o dos slots da interface, que `slot-drag.ts`
 * dirige por ponteiro para funcionar no dedo. Os dois querem a mesma coisa — um ícone
 * seguindo o cursor — e nunca ao mesmo tempo: o dedo é um só, e um arraste começa onde o
 * outro não está.
 *
 * Uma instância por dono deixaria dois `<div>` no `body` fazendo o mesmo trabalho, e o preço
 * não seria o nó a mais: seria o **tempo de vida**. A cena desce e sobe a cada troca de
 * região, e quem destruísse o ghost junto com ela levaria embora o nó que a interface fora da
 * cena — mochila, mesa de troca, banco — continua usando. O gesto seguinte moveria um ícone
 * que ninguém vê, e só no celular, e só depois de atravessar um portal.
 *
 * ## Por que uma função e não uma constante exportada
 *
 * O nó só pode ser criado **depois** do `body`. Este módulo é importado no topo do grafo, e
 * um `new DragGhost()` no corpo dele rodaria antes de haver onde pendurar o elemento.
 */
let shared: DragGhost | undefined;

export function dragGhost(): DragGhost {
  shared ??= new DragGhost();
  return shared;
}
